import { BlockchainID, IApiUserGroupParams } from 'multirpc-sdk';

import { ALL_WHITELIST_BLOCKCHAINS_VALUE } from 'modules/projects/const';
import { MultiService } from 'modules/api/MultiService';
import { TFAQueryFnParams } from 'modules/tfa/types';
import { createQueryFnWithErrorHandler } from 'store/utils/createQueryFnWithErrorHandler';
import { web3Api } from 'store/queries';
import { mapSharkChainToChainPath } from 'modules/chains/utils/mapSharkChainNameToChainPath';
import { errorHandlerWithNotification } from 'domains/userSettings/actions/utils/errorHandlerWithNotification';
import { RootState } from 'store';
import { selectJWTs } from 'domains/jwtToken/action/fetchJWTs';

import { fetchProjectsWhitelistsBlockchains } from './fetchProjectsWhitelistsBlockchains';
import { filterWhitelistIfNeeded } from './utils/filterWhitelistIfNeeded';
import { mapBtcMainnetChainIdToBtc } from './utils/mapBtcMainnetChainIdToBtc';
import { mapMultichainToAapiV2 } from '../utils/mapMultichainToAapiV2';
import { updateProjectWhitelistBlockchains } from './utils/updateProjectWhitelistBlockchains';

export interface AddBlockchainsToWhitelistParams extends IApiUserGroupParams {
  userEndpointToken: string;
  blockchains: BlockchainID[];
}

export const {
  endpoints: { addBlockchainsToWhitelist },
  useAddBlockchainsToWhitelistMutation,
} = web3Api.injectEndpoints({
  endpoints: build => ({
    addBlockchainsToWhitelist: build.mutation<
      BlockchainID[],
      TFAQueryFnParams<AddBlockchainsToWhitelistParams>
    >({
      queryFn: createQueryFnWithErrorHandler({
        errorHandler: errorHandlerWithNotification,
        queryFn: async (
          { params: { blockchains, group, userEndpointToken }, totp },
          { dispatch },
        ) => {
          const service = MultiService.getService().getAccountingGateway();

          const preparedChains =
            blockchains.length === 0
              ? [ALL_WHITELIST_BLOCKCHAINS_VALUE]
              : blockchains
                  .map(mapBtcMainnetChainIdToBtc)
                  .map(mapMultichainToAapiV2);

          const data = await service.addBlockchainsToWhitelist(
            preparedChains,
            {
              token: userEndpointToken,
              group,
            },
            totp,
          );

          // Check whitelist and filter out removed chains
          await filterWhitelistIfNeeded({
            userEndpointToken,
            group,
            currentBlockchains: preparedChains,
            dispatch,
          });

          return { data };
        },
      }),
      onQueryStarted: async (
        { params: { group, userEndpointToken } },
        { dispatch, getState, queryFulfilled },
      ) => {
        const { data: updatedBlockchains } = await queryFulfilled;

        updateProjectWhitelistBlockchains({
          blockchains: updatedBlockchains.map(mapSharkChainToChainPath),
          dispatch,
          teamAddress: group,
          userEndpointToken,
        });

        const projects = selectJWTs(getState() as RootState, { group });

        dispatch(
          fetchProjectsWhitelistsBlockchains.initiate(
            { group, projects },
            { forceRefetch: true },
          ),
        );
      },
    }),
  }),
  overrideExisting: true,
});
